/**
 * lyrics-autodraft — turn PLAIN pasted lyrics into a first-pass timed timeline
 * by looking at the audio, so the user starts from something close instead of
 * from nothing.
 *
 * WHAT THIS IS HONEST ABOUT
 * This does NOT know where the vocals are. Nothing cheap does: on a finished
 * mix the instruments occupy the same band as the voice, so an energy envelope
 * cannot separate "singing" from "loud guitar". Automatic speech recognition
 * could, but it returns garbled words on singing (see the note at the top of
 * auto-captions-align.js) and costs a 40–75 MB model download — which the
 * person who asked for this feature would pay for on Android over 4G.
 *
 * So this solves the three things an energy envelope CAN answer, which happen
 * to be the three that ruin a naive even spread:
 *
 *   1. The song does not start at 0:00. Almost every track opens with an
 *      instrumental intro; spreading lines from t=0 puts the first line several
 *      seconds early and every later line drifts with it.
 *   2. Songs have breaks. A solo or a silent bar in the middle gets lyric lines
 *      smeared across it, and the whole second half lands late.
 *   3. Lines are not equal. "Oh" and a fourteen-word line should not get the
 *      same slice of time, so each line is weighted by its length.
 *
 * Everything past that is the user's call, via tap-sync or the ± nudge. This
 * output is explicitly labelled a draft in the UI — never presented as synced.
 *
 * The envelope is plain RMS, not spectral flux. Flux measures onsets (it fires
 * on every drum hit), and what we need here is "is there sound at all" — RMS
 * answers that at a fraction of the cost and with no FFT.
 *
 *   LyricsAutodraft.draft(text, {
 *       channelData, sampleRate, durationS,   // from the decoded AudioBuffer
 *       introS,                               // optional user override
 *   }) -> [{startS, endS, text, words:null}]  // KaraokeLyrics timeline shape
 *
 * Pure and synchronous — a 4-minute track at 20 Hz is ~4800 envelope samples,
 * so this runs in single-digit milliseconds. Exposed as window.LyricsAutodraft
 * and module.exports (node tests).
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.LyricsAutodraft = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var ENV_HZ = 20;          // 50 ms resolution — enough to find gaps
    var MIN_GAP_S = 2.0;      // shorter quiet spells are musical, not a break
    var MIN_LINE_S = 0.7;     // a line below this is unreadable
    var QUIET_FRAC = 0.12;    // of the 90th-percentile level

    /* ── RMS envelope at ENV_HZ ─────────────────────────────────────────── */
    function envelope(channelData, sampleRate, hz) {
        hz = hz || ENV_HZ;
        var hop = Math.max(1, Math.round(sampleRate / hz));
        var frames = Math.max(1, Math.floor(channelData.length / hop));
        var env = new Float32Array(frames);
        for (var f = 0; f < frames; f++) {
            var base = f * hop, sum = 0, n = 0;
            var end = Math.min(base + hop, channelData.length);
            for (var i = base; i < end; i++) { var v = channelData[i]; sum += v * v; n++; }
            env[f] = n ? Math.sqrt(sum / n) : 0;
        }
        return env;
    }

    /* Robust loudness reference: the 90th percentile, so one clipped transient
       cannot raise the threshold and swallow the whole quiet verse. */
    function percentile(env, p) {
        var arr = Array.prototype.slice.call(env).sort(function (a, b) { return a - b; });
        if (!arr.length) return 0;
        var k = Math.min(arr.length - 1, Math.max(0, Math.round((arr.length - 1) * p)));
        return arr[k];
    }

    /* ── Spans of audible audio, with intro / outro / breaks removed ─────── */
    function activeSpans(env, hz, durationS, opts) {
        opts = opts || {};
        var minGapS = opts.minGapS != null ? opts.minGapS : MIN_GAP_S;
        var quietFrac = opts.quietFrac != null ? opts.quietFrac : QUIET_FRAC;
        var thresh = percentile(env, 0.9) * quietFrac;

        // Frame-level loud/quiet, then collapse quiet runs that are long enough
        // to count as a gap. Short quiet runs stay INSIDE a span: a rest between
        // two phrases is not a break.
        var spans = [], cur = null;
        var frameS = 1 / hz;
        for (var f = 0; f < env.length; f++) {
            var loud = env[f] > thresh;
            if (loud) {
                if (!cur) cur = { startS: f * frameS, endS: (f + 1) * frameS };
                else cur.endS = (f + 1) * frameS;
            } else if (cur) {
                // Look ahead: is this the start of a long enough quiet run?
                var q = f, need = Math.ceil(minGapS * hz);
                while (q < env.length && env[q] <= thresh) q++;
                if ((q - f) >= need) { spans.push(cur); cur = null; f = q - 1; }
                else { f = q - 1; }   // short rest — keep the span open
            }
        }
        if (cur) spans.push(cur);

        // Nothing crossed the threshold (a silent or a very quiet file): treat
        // the whole track as active rather than returning an empty draft.
        if (!spans.length) return [{ startS: 0, endS: durationS || (env.length / hz) }];

        if (durationS > 0) {
            for (var s = 0; s < spans.length; s++) {
                spans[s].startS = Math.max(0, Math.min(spans[s].startS, durationS));
                spans[s].endS = Math.max(spans[s].startS, Math.min(spans[s].endS, durationS));
            }
        }
        return spans.filter(function (sp) { return sp.endS > sp.startS; });
    }

    function splitLines(text) {
        return String(text == null ? '' : text)
            .split(/\r?\n/)
            .map(function (l) { return l.trim(); })
            .filter(function (l) { return l.length > 0; });
    }

    /* Length weight. Word count plus a character term: a long word takes longer
       to sing than a short one, but not proportionally to its letters. */
    function weightOf(line) {
        var words = line.split(/\s+/).filter(Boolean).length;
        return Math.max(1, words + line.replace(/\s+/g, '').length * 0.12);
    }

    /**
     * Build the draft timeline.
     */
    function draft(text, opts) {
        opts = opts || {};
        var lines = splitLines(text);
        if (!lines.length) return [];

        var hz = opts.hz || ENV_HZ;
        var durationS = opts.durationS || 0;
        var spans;

        if (opts.channelData && opts.sampleRate) {
            var env = envelope(opts.channelData, opts.sampleRate, hz);
            if (!durationS) durationS = opts.channelData.length / opts.sampleRate;
            spans = activeSpans(env, hz, durationS, opts);
        } else {
            // No audio available — fall back to one span over the whole track,
            // which degrades to a length-weighted even spread.
            spans = [{ startS: 0, endS: durationS || lines.length }];
        }

        // A user-supplied vocal start wins over the detected one: they can hear
        // the song, we can only see its energy.
        if (opts.introS != null && opts.introS > 0) {
            spans = spans
                .map(function (sp) { return { startS: Math.max(sp.startS, opts.introS), endS: sp.endS }; })
                .filter(function (sp) { return sp.endS > sp.startS; });
            if (!spans.length) spans = [{ startS: opts.introS, endS: Math.max(opts.introS + lines.length, durationS) }];
        }

        var totalActive = spans.reduce(function (a, sp) { return a + (sp.endS - sp.startS); }, 0);
        var weights = lines.map(weightOf);
        var totalWeight = weights.reduce(function (a, w) { return a + w; }, 0) || 1;
        var minLineS = opts.minLineS != null ? opts.minLineS : MIN_LINE_S;

        // Walk the spans, consuming each line's share of the ACTIVE time. A line
        // never straddles a gap: if its share runs past the end of a span it is
        // clipped there and the next line opens in the next span, which is what
        // keeps an instrumental break free of text.
        var out = [];
        var si = 0, cursor = spans[0].startS;
        for (var i = 0; i < lines.length; i++) {
            var share = Math.max(minLineS, totalActive * (weights[i] / totalWeight));
            if (si >= spans.length) {
                // Ran out of audio (more lines than the weighting predicted):
                // pack the remainder at the end so nothing is silently dropped.
                var tail = spans[spans.length - 1].endS;
                out.push({ startS: tail, endS: tail + minLineS, text: lines[i], words: null });
                continue;
            }
            if (cursor >= spans[si].endS) {
                si++;
                if (si >= spans.length) { i--; continue; }
                cursor = spans[si].startS;
            }
            var start = cursor;
            var end = Math.min(start + share, spans[si].endS);
            if (end - start < minLineS * 0.5 && si + 1 < spans.length) {
                // Too little room left in this span to be readable — move on.
                si++; cursor = spans[si].startS; i--; continue;
            }
            out.push({ startS: start, endS: end, text: lines[i], words: null });
            cursor = end;
        }

        // endS of each line = the next line's start, matching parseLRC so every
        // downstream consumer (painter, serializer) sees one timeline shape.
        out.sort(function (a, b) { return a.startS - b.startS; });
        for (var k = 0; k < out.length; k++) {
            out[k].endS = (k + 1 < out.length) ? out[k + 1].startS : Math.max(out[k].endS, out[k].startS + 4);
        }
        return out;
    }

    /* Detected start of audible audio — the UI shows this as "vocals start
       about here" so the user can accept or override it. */
    function detectIntroS(channelData, sampleRate, opts) {
        if (!channelData || !sampleRate) return 0;
        var hz = (opts && opts.hz) || ENV_HZ;
        var env = envelope(channelData, sampleRate, hz);
        var durationS = channelData.length / sampleRate;
        var spans = activeSpans(env, hz, durationS, opts || {});
        return spans.length ? spans[0].startS : 0;
    }

    return {
        draft: draft,
        detectIntroS: detectIntroS,
        _envelope: envelope,
        _activeSpans: activeSpans,
        _weightOf: weightOf,
        _splitLines: splitLines,
        ENV_HZ: ENV_HZ,
    };
});
